<!--
  LwSectionTabs: the row of page links under a section's heading (spec 49
  section 4.2).

  NOT LwTabs. LwTabs is a `role="tablist"` of buttons that switch panels in
  place, which is right for the views of one page (Billing's Unbilled /
  Billed / Batch) and wrong for navigation between pages: a screen reader
  told "tab, 2 of 5" expects the next panel, not a new URL and a new
  document title. These are links in a labelled <nav>, each its own route,
  so a copied URL opens on the right page and back/forward step between them.
  vue-router marks the current one; it is given `aria-current="page"` the
  way the rail's links are.

  It is drawn to read as the same family as LwTabs -- same type, same amber
  rule under the current one, same pill for a count, same horizontal scroll
  when the row is wider than the viewport -- because the two sit one above
  the other on pages that have both, and a person should not have to learn
  two tab looks to tell which row changes the page.
-->
<template>
  <nav class="lw-c-section-tabs" :aria-label="label">
    <div class="lw-c-section-tabs__scroller">
      <ul class="lw-c-section-tabs__list">
        <li v-for="tab in tabs" :key="tab.name" class="lw-c-section-tabs__item">
          <router-link
            v-slot="{ href, navigate, isActive }"
            :to="tab.to || { name: tab.name }"
            custom
          >
            <a
              class="lw-c-section-tabs__tab"
              :class="{ 'lw-c-section-tabs__tab--active': isActive }"
              :href="href"
              :aria-current="isActive ? 'page' : null"
              :data-section-tab="tab.name"
              @click="navigate"
            >
              <span class="lw-c-section-tabs__label">{{ tab.label }}</span>
              <span v-if="hasCount(tab)" class="lw-c-section-tabs__count">{{ tab.count }}</span>
            </a>
          </router-link>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script>
export default {
  name: 'LwSectionTabs',
  props: {
    // [{ name, label, count?, to? }]: `name` is the route name the tab opens
    // (and its key); `to` overrides the location when a tab needs query or
    // params as well.
    tabs: {
      type: Array,
      required: true,
      validator: value =>
        value.every(tab => tab && typeof tab.name === 'string' && typeof tab.label === 'string'),
    },
    // The landmark's accessible name ("Team sections"). Required: a page can
    // carry several <nav>s and an unlabelled one is not navigable.
    label: {
      type: String,
      required: true,
    },
  },
  methods: {
    hasCount(tab) {
      return tab.count !== null && tab.count !== undefined;
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

.lw-c-section-tabs {
  background: var(--lw-bg-paper);
  border-bottom: 1px solid var(--lw-border-subtle);
}

.lw-c-section-tabs__scroller {
  overflow-x: auto;
  padding: 0 var(--lw-space-5);
}

.lw-c-section-tabs__list {
  display: flex;
  gap: var(--lw-space-2);
  min-width: max-content;
  margin: 0;
  padding: 0;
  list-style: none;
}

.lw-c-section-tabs__tab {
  display: inline-flex;
  align-items: center;
  gap: var(--lw-space-2);
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  padding: var(--lw-space-3) var(--lw-space-3);
  font: var(--lw-type-label);
  color: var(--lw-fg-secondary);
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    color: var(--lw-fg-primary);
  }

  @include lw-focus-visible;
}

.lw-c-section-tabs__tab--active {
  color: var(--lw-fg-primary);
  border-bottom-color: var(--lw-amber-500);
}

.lw-c-section-tabs__count {
  min-width: var(--lw-space-5);
  padding: 0 var(--lw-space-2);
  border-radius: var(--lw-radius-pill);
  background: var(--lw-bg-sunken);
  color: var(--lw-fg-secondary);
  font: var(--lw-type-caption-strong);
  font-variant-numeric: var(--lw-type-data-numeric);
  text-align: center;
}

.lw-c-section-tabs__tab--active .lw-c-section-tabs__count {
  background: var(--lw-amber-100);
  color: var(--lw-amber-800);
}
</style>
